/* eslint-disable indent */
'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import {
  Search,
  Calendar,
  User,
  Clock,
  Filter,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  X,
  Bookmark,
  BookmarkCheck,
  Share2,
  BookmarkPlus,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '../ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Skeleton from 'react-loading-skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import ImageLoader from '../ui/image-loader';
import { authService } from '@/services/auth.service';
import Loading from '@/app/loading';

// Filter options
const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'title_asc', label: 'Title (A-Z)' },
  { value: 'title_desc', label: 'Title (Z-A)' },
];

export default function News() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isLoading, setIsLoading] = useState(true);
  const [visibleArticles, setVisibleArticles] = useState(10);
  const [hoveredCard, setHoveredCard] = useState(null);
  const tabsRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarkCount, setBookmarkCount] = useState(0);
  const [shareResult, setShareResult] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();

  // Currently applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    sortOption: 'newest',
    authors: [],
    dateRange: { from: '', to: '' },
  });

  // Temporary filters (used in the dialog)
  const [tempFilters, setTempFilters] = useState({
    sortOption: 'newest',
    authors: [],
    dateRange: { from: '', to: '' },
  });

  // Fetch Article
  const fetchArticles = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await authService.getArticles();

      if (response?.data?.items?.length) {
        const apiData = response.data;
        setArticles(apiData.items);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Extract unique categories for filtering
  const categories = [
    'All',
    ...new Set(articles.map((article) => article.category)),
  ];

  const authors = [...new Set(articles.map((article) => article.author))];

  // Simulate loading state
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setIsLoading(false);
  //   }, 1000);

  //   return () => clearTimeout(timer);
  // }, []);

  // Initialize temp filters when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      setTempFilters({
        sortOption: appliedFilters.sortOption,
        authors: [...appliedFilters.authors],
        dateRange: { ...appliedFilters.dateRange },
      });
    }
  }, [dialogOpen, appliedFilters]);

  useEffect(() => {
    // Load bookmarks from localStorage
    const savedBookmarks = JSON.parse(
      localStorage.getItem('xspace_bookmarks') || '[]',
    );
    setBookmarks(savedBookmarks);
    setBookmarkCount(savedBookmarks.length);
  }, []);

  // Update visible cards based on screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter articles based on search term, category, authors, and date range
  const filteredArticles = articles.filter((article) => {
    const matchesSearch =
      article?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article?.meta_description
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === 'All' || article.category === selectedCategory;

    const matchesAuthor =
      appliedFilters.authors.length === 0 ||
      appliedFilters.authors.includes(article.author);

    function parseArticleDate(dateStr) {
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    }

    function parseFilterDate(dateStr) {
      return new Date(dateStr);
    }

    const fromDate =
      appliedFilters.dateRange.from === ''
        ? null
        : parseFilterDate(appliedFilters.dateRange.from);
    const toDate =
      appliedFilters.dateRange.to === ''
        ? null
        : parseFilterDate(appliedFilters.dateRange.to);
    const articleDate = parseArticleDate(article.date);

    const matchesDateRange =
      (fromDate === null || articleDate >= fromDate) &&
      (toDate === null || articleDate <= toDate);

    return (
      matchesSearch && matchesCategory && matchesAuthor && matchesDateRange
    );
  });

  // Sort articles
  const sortedArticles = [...filteredArticles].sort((a, b) => {
    switch (appliedFilters.sortOption) {
      case 'newest':
        return new Date(b.sorting).getTime() - new Date(a.sorting).getTime();
      case 'oldest':
        return new Date(a.sorting).getTime() - new Date(b.sorting).getTime();
      case 'title_asc':
        return a.title.localeCompare(b.title);
      case 'title_desc':
        return b.title.localeCompare(a.title);
      default:
        return 0;
    }
  });

  // Separate featured articles
  const featuredArticles = sortedArticles.filter((article) => article.featured);
  const regularArticles = sortedArticles.filter((article) => !article.featured);

  // Articles to display with pagination
  const displayedArticles = regularArticles.slice(0, visibleArticles);

  // Load more articles
  const handleLoadMore = () => {
    setVisibleArticles((prev) => prev + 10);
  };

  function formatDate(dateString) {
    const [day, month, year] = dateString.split('/');
    const date = new Date(`${year}-${month}-${day}`);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // Toggle author selection in temp filters
  const toggleAuthor = (author) => {
    setTempFilters((prev) => ({
      ...prev,
      authors: prev.authors.includes(author)
        ? prev.authors.filter((a) => a !== author)
        : [...prev.authors, author],
    }));
  };

  // Update temp date range
  const updateTempDateRange = (field, value) => {
    setTempFilters((prev) => ({
      ...prev,
      dateRange: { ...prev.dateRange, [field]: value },
    }));
  };

  // Update temp sort option
  const updateTempSortOption = (value) => {
    setTempFilters((prev) => ({
      ...prev,
      sortOption: value,
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setAppliedFilters({
      sortOption: tempFilters.sortOption,
      authors: [...tempFilters.authors],
      dateRange: { ...tempFilters.dateRange },
    });
    setDialogOpen(false);
  };

  // Reset filters
  const resetFilters = () => {
    const defaultFilters = {
      sortOption: 'newest',
      authors: [],
      dateRange: { from: '', to: '' },
    };
    setTempFilters(defaultFilters);
  };

  // Reset all filters including search and category
  const resetAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('All');
    setAppliedFilters({
      sortOption: 'newest',
      authors: [],
      dateRange: { from: '', to: '' },
    });
  };

  // Scroll tabs
  const scrollTabs = (direction) => {
    if (tabsRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      tabsRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // const sortedArticles = useMemo(() => {
  //   return [...regularArticles].sort((a, b) => b.sort - a.sort);
  // }, [regularArticles]);

  // Check if any filters are applied
  const hasActiveFilters =
    appliedFilters.authors.length > 0 ||
    appliedFilters.dateRange.from !== '' ||
    appliedFilters.dateRange.to !== '' ||
    appliedFilters.sortOption !== 'newest';

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  // Shimmer effect for cards
  const shimmer = {
    hidden: { x: '-100%', opacity: 0.1 },
    visible: {
      x: '100%',
      opacity: 0.3,
      transition: {
        repeat: Number.POSITIVE_INFINITY,
        duration: 1.5,
        ease: 'linear',
        repeatDelay: 0.5,
      },
    },
  };

  const highlightText = (text, search) => {
    if (!search || typeof text !== 'string') return text;
    const regex = new RegExp(`(${search})`, 'gi');
    return text.split(regex).map((part, index) =>
      part.toLowerCase() === search.toLowerCase() ? (
        <span key={index} className='bg-yellow-300 dark:bg-yellow-600'>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  // Extract plain text from JSX description for preview
  const getPlainTextPreview = (description, maxLength = 150) => {
    if (typeof description === 'string') {
      return description.length > maxLength
        ? description.substring(0, maxLength) + '...'
        : description;
    }

    // If it's a React element, try to extract text from its children
    let text = '';
    try {
      // Convert to string and strip HTML tags
      const stringVersion = JSON.stringify(description);
      text = stringVersion
        .replace(/<[^>]*>?/gm, '')
        .replace(/['"{}:,]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      return text.length > maxLength
        ? text.substring(0, maxLength) + '...'
        : text;
    } catch (e) {
      return 'Read more about this exciting update...';
    }
  };

  // Toggle bookmark
  const toggleBookmark = (article) => {
    const bookmarks = JSON.parse(
      localStorage.getItem('xspace_bookmarks') || '[]',
    );
    const isBookmarked = bookmarks.some(
      (bookmark) => bookmark.slug === article.slug,
    );

    if (isBookmarked) {
      // Remove bookmark
      const updatedBookmarks = bookmarks.filter(
        (bookmark) => bookmark.slug !== article.slug,
      );
      localStorage.setItem(
        'xspace_bookmarks',
        JSON.stringify(updatedBookmarks),
      );
      setBookmarks(updatedBookmarks);
      setBookmarkCount(updatedBookmarks.length);
    } else {
      // Add bookmark
      const newBookmark = {
        id: article._id,
        title: article.title,
        slug: article.slug,
        image: article.image_url,
        date: article.date,
        category: article.category,
        author: article.author,
      };
      const updatedBookmarks = [...bookmarks, newBookmark];
      localStorage.setItem(
        'xspace_bookmarks',
        JSON.stringify(updatedBookmarks),
      );
      setBookmarks(updatedBookmarks);
      setBookmarkCount(updatedBookmarks.length);
    }
  };

  // Share article
  // const shareArticle = (article) => {
  //   const shareUrl = `https://www.x-space.ae/articles/${article.slug}`;

  //   const shareData = {
  //     title: article.title,
  //     text: article.meta_description,
  //     url: window.location.href,
  //   };

  //   if (navigator.share) {
  //     navigator
  //       .share({
  //         title: article.title,
  //         text: article.meta_description,
  //         url: shareUrl,
  //       })
  //       .catch((err) => {
  //         console.error('Error sharing:', err);
  //         // Fallback to copy to clipboard
  //         navigator.clipboard.writeText(shareUrl);
  //         alert('Link copied to clipboard!');
  //       });
  //   } else {
  //     // Fallback for browsers that don't support Web Share API
  //     navigator.clipboard.writeText(shareUrl);
  //     alert('Link copied to clipboard!');
  //   }
  // };

  const shareArticle = async (article) => {
    const shareUrl = `https://www.x-space.ae/articles/${article.slug}`;

    const shareData = {
      title: article.title,
      text: article.meta_description,
      url: shareUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        setShareResult({ success: true, message: 'Shared successfully!' });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShareResult({ success: false, message: 'Error sharing' });
        }
      }
    } else {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(shareUrl);
        } else {
          const input = document.createElement('input');
          input.value = shareUrl;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }
        setShareResult({ success: true, message: 'Link copied to clipboard!' });
      } catch (err) {
        setShareResult({ success: false, message: 'Failed to copy link' });
      }
    }

    // Reset message after 3s
    setTimeout(() => setShareResult(null), 3000);
  };

  // Check if article is bookmarked
  const isArticleBookmarked = (slug) => {
    return bookmarks.some((bookmark) => bookmark.slug === slug);
  };

  // Go to bookmarks page
  const goToBookmarks = () => {
    router.push('/bookmarks');
  };

  // if (isLoading) return <Loading />;

  return (
    <>
      {isLoading && <Loading className='fixed inset-0 z-50 bg-white/80' />}

      <div className='min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 overflow-hidden pt-20'>
        {/* Hero Section */}
        <section className='relative h-[50vh] md:h-[50vh] bg-gradient-to-r from-[#38F5EF]/90 to-[#FE2A53]/90 overflow-hidden'>
          <div className='absolute inset-0 bg-black/50 z-10'></div>
          <div className='absolute inset-0 z-0'>
            <Image
              // src='https://images.x-space.ae/assets/images/News pic/8.webp'
              src='https://images.x-space.ae/assets/images/website_image/home_page_1.jpg'
              alt='Dubai Skyline'
              fill
              className='object-cover'
              priority
              sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
            />
          </div>
          <div className='container mx-auto px-4 h-full flex flex-col justify-center items-center relative z-20'>
            <h1 className='text-2xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-4'>
              X-Space Real Estate Articles
            </h1>
            <p className='text-lg md:text-2xl text-white/90 text-center max-w-3xl mb-8'>
              Stay informed with the latest insights, market trends, and
              investment opportunities in Dubai's dynamic property market
            </p>
            <div className='relative w-full max-w-2xl'>
              <Input
                type='text'
                placeholder='Search articles...'
                className='pl-12 pr-4 py-6 rounded-full text-lg shadow-xl border-0 dark:bg-white'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search
                className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400'
                size={20}
              />
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className='container mx-auto px-4 py-12 '>
          {/* Category Tabs with Horizontal Scroll */}
          <div className='mb-12'>
            <div className='flex items-center justify-between mb-6'>
              <h3 className='text-2xl md:text-3xl font-bold text-primarycolor dark:text-secondarycolor'>
                Browse by Category
              </h3>

              {/* Filter and Bookmark Section */}
              {/* <div className='flex justify-between items-center mb-8'>
              <Button
                variant='outline'
                size='sm'
                className='gap-2 border-[#FE2A53] text-[#FE2A53]'
                onClick={goToBookmarks}
              >
                <BookmarkPlus size={16} />
                Bookmarks
                {bookmarkCount > 0 && (
                  <Badge className='ml-1 h-5 w-5 p-0 flex items-center justify-center bg-[#FE2A53] text-white'>
                    {bookmarkCount}
                  </Badge>
                )}
              </Button>
            </div> */}

              {/* Filter Dialog */}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <div className='flex justify-end gap-2'>
                  {/* <DialogTrigger asChild>
                  <Button
                    variant={hasActiveFilters ? 'default' : 'outline'}
                    size='sm'
                    className={`${hasActiveFilters ? 'bg-[#FE2A53]' : ''}`}
                  >
                    <Filter size={16} />
                    Filter{' '}
                    {hasActiveFilters &&
                      `(${
                        appliedFilters.authors.length +
                        (appliedFilters.dateRange.from ? 1 : 0) +
                        (appliedFilters.dateRange.to ? 1 : 0) +
                        (appliedFilters.sortOption !== 'newest' ? 1 : 0)
                      })`}
                  </Button>
                </DialogTrigger> */}

                  <DialogTrigger asChild>
                    <Button
                      variant={hasActiveFilters ? 'default' : 'outline'}
                      size='sm'
                      className={`${hasActiveFilters ? 'bg-[#FE2A53]' : ''}`}
                    >
                      <Filter size={16} />
                      Filter{' '}
                      {hasActiveFilters &&
                        `(${
                          appliedFilters.authors.length +
                          (appliedFilters.dateRange.from ? 1 : 0) +
                          (appliedFilters.dateRange.to ? 1 : 0) +
                          (appliedFilters.sortOption !== 'newest' ? 1 : 0)
                        })`}
                    </Button>
                  </DialogTrigger>

                  {/* <DialogTrigger asChild> */}
                  <Button
                    variant='outline'
                    size='sm'
                    className='bg-primarycolor hover:bg-secondarycolor text-white hover:text-black  dark:bg-primarycolor dark:hover:bg-secondarycolor dark:text-white dark:hover:text-black  '
                    onClick={goToBookmarks}
                  >
                    <BookmarkPlus size={16} />
                    Bookmarks
                    {bookmarkCount > 0 && (
                      <Badge className='ml-1 h-5 w-5 p-0 flex items-center justify-center bg-[#FE2A53] text-white'>
                        {bookmarkCount}
                      </Badge>
                    )}
                  </Button>
                  {/* </DialogTrigger> */}
                </div>

                <DialogContent className='sm:max-w-[425px]'>
                  <DialogHeader>
                    <DialogTitle>Filter Articles</DialogTitle>
                  </DialogHeader>
                  <div className='grid gap-4 py-4'>
                    <div className='grid gap-2'>
                      <Label htmlFor='sort'>Sort By</Label>
                      <Select
                        value={tempFilters.sortOption}
                        onValueChange={updateTempSortOption}
                      >
                        <SelectTrigger id='sort'>
                          <SelectValue placeholder='Sort by' />
                        </SelectTrigger>
                        <SelectContent>
                          {sortOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='grid gap-2'>
                      <Label>Date Range</Label>
                      <div className='grid grid-cols-2 gap-2'>
                        <div>
                          <Label htmlFor='from' className='text-xs'>
                            From
                          </Label>
                          <Input
                            id='from'
                            type='date'
                            value={tempFilters.dateRange.from}
                            onChange={(e) =>
                              updateTempDateRange('from', e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label htmlFor='to' className='text-xs'>
                            To
                          </Label>
                          <Input
                            id='to'
                            type='date'
                            value={tempFilters.dateRange.to}
                            onChange={(e) =>
                              updateTempDateRange('to', e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className='grid gap-2'>
                      <Label>Authors</Label>
                      <div className='h-[200px] overflow-y-auto border rounded-md p-2'>
                        {authors.map((author, index) => (
                          <div
                            key={`${author}-${index}`}
                            className='flex items-center space-x-2 py-1'
                          >
                            <Checkbox
                              id={`author-${author}`}
                              checked={tempFilters.authors.includes(author)}
                              onCheckedChange={() => toggleAuthor(author)}
                            />
                            <Label
                              htmlFor={`author-${author}`}
                              className='text-sm cursor-pointer'
                            >
                              {author}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className='flex justify-between'>
                    <Button variant='outline' onClick={resetFilters}>
                      Reset Filters
                    </Button>
                    <Button onClick={applyFilters}>Apply Filters</Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Share/Download result message */}
              <AnimatePresence>
                {shareResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`mt-4 p-2 rounded-lg text-center text-sm ${
                      shareResult.success
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}
                  >
                    <div className='flex items-center justify-center gap-1'>
                      {shareResult.success ? (
                        <Check
                          size={14}
                          className='text-green-600 dark:text-green-400'
                        />
                      ) : (
                        <X
                          size={14}
                          className='text-red-600 dark:text-red-400'
                        />
                      )}
                      {shareResult.message}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Horizontally scrollable tabs with navigation buttons */}
            <div className='relative'>
              {/* Left scroll button */}
              {/* <Button
              onClick={() => scrollTabs('left')}
              className='absolute left-0 top-1/2 transform -translate-y-1/2 z-20 bg-white rounded-full shadow-lg p-2 ml-2 md:hidden'
              aria-label='Scroll left'
            >
              <ChevronLeft size={20} />
            </Button> */}

              {/* Scrollable Tabs container */}
              <div
                className='overflow-x-auto bg-gray-100 p-1 text-gray-600 rounded-md shadow-inner'
                ref={tabsRef}
              >
                <div className='pl-4 pr-4'>
                  <Tabs defaultValue='All' className='w-full'>
                    <TabsList className='w-full max-w-full flex flex-nowrap py-4 justify-start'>
                      {categories.map((category, index) => (
                        <TabsTrigger
                          key={`${category}-${index}`}
                          value={category}
                          onClick={() => setSelectedCategory(category)}
                          className='min-w-max text-1xl'
                        >
                          {category
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                </div>
              </div>

              {/* Right scroll button */}
              {/* <Button
              onClick={() => scrollTabs('right')}
              className='absolute right-0 top-1/2 transform -translate-y-1/2 z-20 bg-white rounded-full shadow-lg p-2 mr-2 md:hidden'
              aria-label='Scroll right'
            >
              <ChevronRight size={20} />
            </Button> */}
            </div>

            <div
              className={`my-4 text-center text-sm text-gray-500 dark:text-gray-400 ${
                isMobile ? 'block' : 'hidden'
              }`}
            >
              <span className='inline-flex items-center'>
                <ChevronLeft className='w-4 h-4 mr-1' />
                Scroll to explore all categories
                <ChevronRight className='w-4 h-4 ml-1' />
              </span>
            </div>

            {/* Active filters display */}
            {hasActiveFilters && (
              <div className='mt-4 flex flex-wrap items-center gap-2'>
                <span className='text-sm text-gray-500'>Active filters:</span>

                {appliedFilters.sortOption !== 'newest' && (
                  <Badge variant='outline' className='flex items-center gap-1'>
                    Sort:{' '}
                    {
                      sortOptions.find(
                        (opt) => opt.value === appliedFilters.sortOption,
                      )?.label
                    }
                  </Badge>
                )}

                {appliedFilters.dateRange.from && (
                  <Badge variant='outline' className='flex items-center gap-1'>
                    From:{' '}
                    {new Date(
                      appliedFilters.dateRange.from,
                    ).toLocaleDateString()}
                  </Badge>
                )}

                {appliedFilters.dateRange.to && (
                  <Badge variant='outline' className='flex items-center gap-1'>
                    To:{' '}
                    {new Date(appliedFilters.dateRange.to).toLocaleDateString()}
                  </Badge>
                )}

                {appliedFilters.authors.map((author) => (
                  <Badge
                    key={author}
                    variant='outline'
                    className='flex items-center gap-1'
                  >
                    Author: {author}
                  </Badge>
                ))}

                <Button
                  variant='ghost'
                  size='sm'
                  className='text-xs text-gray-500 h-7 px-2'
                  onClick={resetAllFilters}
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>

          {/* Featured Articles */}
          {featuredArticles.length > 0 && (
            <section className='mb-16'>
              <h3 className='text-2xl md:text-3xl font-bold text-primarycolor dark:text-secondarycolor mb-8'>
                Featured Articles
              </h3>
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
                {isLoading ? (
                  <>
                    <FeaturedArticleSkeleton />
                    <FeaturedArticleSkeleton />
                  </>
                ) : (
                  featuredArticles.map((article, index) => (
                    <motion.div
                      key={article.slug}
                      variants={cardVariants}
                      className='group'
                    >
                      <Link
                        rel='preload'
                        href={`/articles/${article.slug}`}
                        key={article._id}
                        className='group'
                      >
                        {/* Shimmer effect overlay */}
                        {hoveredCard === article.slug && (
                          <motion.div
                            className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent z-10 pointer-events-none'
                            variants={shimmer}
                            initial='hidden'
                            animate='visible'
                          />
                        )}

                        <Card className='w-full overflow-hidden transition-all duration-300 hover:shadow-xl border-0 shadow-md bg-white dark:bg-gray-800'>
                          <div className='relative aspect-[3/2] overflow-hidden'>
                            {/* <Image
                            src={article.image || '/placeholder.svg'}
                            alt={article.title}
                            fill
                            className='object-cover transition-transform duration-500 group-hover:scale-105'
                            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                          /> */}
                            <ImageLoader
                              src={
                                article.image_url
                                  ? `${article.image_url}?height=400&width=600`
                                  : 'https://admin-panel.x-space.ae/public/uploads/article/richmond_district_al_furjan_branded_community_launch_dubai/image_94_1775645896962_253272911.webp'
                              }
                              alt={article.title || 'Featured Article'}
                              fill
                              loaderType='hash'
                              objectFit='cover'
                              priority={index === 0}
                            />

                            <div className='absolute top-2 left-2'>
                              <Badge className='bg-[#FE2A53] hover:bg-[#FE2A53]/90'>
                                {article.category
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (char) =>
                                    char.toUpperCase(),
                                  )}
                              </Badge>
                            </div>
                            <div className='absolute top-2 right-2 flex gap-2'>
                              <Button
                                variant='secondary'
                                size='icon'
                                className='bg-white/80 hover:bg-white'
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleBookmark(article);
                                }}
                                aria-label={
                                  isArticleBookmarked(article.slug)
                                    ? 'Remove from bookmarks'
                                    : 'Add to bookmarks'
                                }
                              >
                                {isArticleBookmarked(article.slug) ? (
                                  <BookmarkCheck
                                    size={16}
                                    className='text-[#FE2A53]'
                                  />
                                ) : (
                                  <Bookmark size={16} />
                                )}
                              </Button>
                              <Button
                                variant='secondary'
                                size='icon'
                                className='bg-white/80 hover:bg-white'
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  shareArticle(article);
                                }}
                                aria-label='Share article'
                              >
                                <Share2 size={16} />
                              </Button>
                            </div>
                          </div>
                          <CardHeader>
                            <CardTitle
                              className='text-2xl group-hover:text-[#FE2A53] duration-300 font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 transition-colors'
                              transition={{ duration: 0.3 }}
                            >
                              {/* {article.title} */}
                              {highlightText(article.title, searchTerm)}
                            </CardTitle>
                            <CardDescription className='text-base mt-2 text-gray-600 dark:text-gray-300 line-clamp-3 mb-4 flex-grow'>
                              {getPlainTextPreview(article.meta_description)}
                            </CardDescription>
                          </CardHeader>
                          <CardFooter className='flex flex-col sm:flex-row justify-between items-start sm:items-center pt-2 sm:pt-0 gap-2'>
                            <div className='flex flex-col sm:flex-row w-full sm:w-auto gap-2'>
                              {/* Top row on mobile: author + date */}
                              <div className='flex flex-row gap-2'>
                                {article?.author && (
                                  <div className='mr-2 flex items-center gap-1 text-sm text-gray-500'>
                                    <User size={14} />
                                    <span>{article.author}</span>
                                  </div>
                                )}
                                {article?.date && (
                                  <div className='mr-2 flex items-center gap-1 text-sm text-gray-500'>
                                    <Calendar size={14} />
                                    <span className='text-gray-600 dark:text-gray-300'>
                                      {formatDate(article.date)}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Bottom row on mobile: read time */}
                              {article?.read_time && (
                                <div className='flex items-center gap-1 text-sm text-gray-500'>
                                  <Clock size={14} />
                                  <span>{article.read_time}</span>
                                </div>
                              )}
                            </div>

                            <Button
                              variant='ghost'
                              size='sm'
                              className='mt-2 sm:mt-0 gap-1 text-[#FE2A53] hover:bg-secondarycolor dark:hover:bg-secondarycolor dark:hover:text-black'
                            >
                              Read more <ArrowUpRight size={14} />
                            </Button>
                          </CardFooter>
                        </Card>
                      </Link>
                    </motion.div>
                  ))
                )}
              </div>
            </section>
          )}

          {/* All Articles with Load More */}
          <section>
            <h4 className='text-2xl md:text-3xl font-bold text-primarycolor dark:text-secondarycolor mb-8'>
              Latest Articles
            </h4>
            {isLoading ? (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <ArticleSkeleton key={i} />
                ))}
              </div>
            ) : displayedArticles.length > 0 ? (
              <>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                  {displayedArticles.map((article, index) => (
                    <Link
                      rel='preload'
                      href={`/articles/${article.slug}`}
                      key={article._id}
                      className='group'
                    >
                      <Card className='overflow-hidden h-full transition-all duration-300 hover:shadow-lg border-0 shadow-sm bg-white dark:bg-gray-800 '>
                        <div className='relative h-64 overflow-hidden'>
                          {/* <Image
                          src={article.image || '/placeholder.svg'}
                          alt={article.title}
                          fill
                          className='object-cover transition-transform duration-500 group-hover:scale-105'
                          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                        /> */}

                          <ImageLoader
                            src={
                              `${article.image_url}?height=400&width=600` ||
                              '/placeholder.svg'
                            }
                            alt={article.title || 'Latest Article'}
                            fill
                            className='object-cover transition-transform duration-500'
                            loaderType='hash'
                            objectFit='cover'
                            priority={index === 0}
                          />

                          <div className='absolute top-2 left-2'>
                            <Badge className='bg-[#FE2A53] hover:bg-[#FE2A53]/90'>
                              {article.category
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                            </Badge>
                          </div>
                          <div className='absolute top-2 right-2 flex gap-2'>
                            <Button
                              variant='secondary'
                              size='icon'
                              className='bg-white/80 hover:bg-white'
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleBookmark(article);
                              }}
                              aria-label={
                                isArticleBookmarked(article.slug)
                                  ? 'Remove from bookmarks'
                                  : 'Add to bookmarks'
                              }
                            >
                              {isArticleBookmarked(article.slug) ? (
                                <BookmarkCheck
                                  size={16}
                                  className='text-[#FE2A53]'
                                />
                              ) : (
                                <Bookmark size={16} />
                              )}
                            </Button>
                            <Button
                              variant='secondary'
                              size='icon'
                              className='bg-white/80 hover:bg-white'
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                shareArticle(article);
                              }}
                              aria-label='Share article'
                            >
                              <Share2 size={16} />
                            </Button>
                          </div>
                        </div>
                        <CardHeader>
                          <CardTitle className='text-xl group-hover:text-[#FE2A53] text-gray-600 dark:text-gray-300 line-clamp-3 flex-grow'>
                            {highlightText(article.title, searchTerm)}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className='text-gray-600 line-clamp-2 dark:text-gray-300 '>
                            {getPlainTextPreview(article.meta_description)}
                          </p>
                        </CardContent>

                        <CardFooter className='flex flex-col sm:flex-row justify-between items-start sm:items-center pt-2 gap-2'>
                          {/* Left side: author + date + read time */}
                          <div className='flex flex-col sm:flex-row w-full sm:w-auto gap-1 sm:gap-4'>
                            {/* Top row on mobile: author + date */}
                            <div className='flex flex-row gap-2'>
                              {article?.author && (
                                <div className='flex items-center gap-1 text-sm text-gray-500'>
                                  <User size={14} />
                                  <span>{article.author}</span>
                                </div>
                              )}
                              {article?.date && (
                                <div className='flex items-center gap-1 text-sm text-gray-500'>
                                  <Calendar size={14} />
                                  <span className='dark:text-gray-300'>
                                    {formatDate(article.date)}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Bottom row on mobile: read time */}
                            {article?.read_time && (
                              <div className='flex items-center gap-1 text-sm text-gray-500'>
                                <Clock size={14} />
                                <span>{article.read_time}</span>
                              </div>
                            )}
                          </div>

                          {/* Mobile-only button */}
                          <Button
                            variant='ghost'
                            size='sm'
                            className='mt-2 sm:mt-0 gap-1 text-[#FE2A53] hover:bg-secondarycolor dark:hover:bg-secondarycolor dark:hover:text-black flex-nowrap  sm:hidden'
                          >
                            Read more <ArrowUpRight size={14} />
                          </Button>
                        </CardFooter>
                      </Card>
                    </Link>
                  ))}
                </div>

                {/* Load More Button */}
                {visibleArticles < regularArticles.length && (
                  <div className='flex justify-center mt-12'>
                    <Button
                      onClick={handleLoadMore}
                      className='bg-primarycolor hover:bg-secondarycolor text-white hover:text-black  dark:bg-primarycolor dark:hover:bg-secondarycolor dark:text-white dark:hover:text-black  py-6'
                    >
                      Load More Articles
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className='text-center py-12'>
                <h3 className='text-xl font-medium text-gray-600 mb-4'>
                  No articles found
                </h3>
                <p className='text-gray-500 mb-6'>
                  Try adjusting your search or filter criteria
                </p>
                <Button onClick={resetAllFilters}>Reset All Filters</Button>
              </div>
            )}
          </section>

          {/* Newsletter Section */}
          <section className='mt-20 bg-gradient-to-r from-[#38F5EF]/10 to-[#FE2A53]/10 dark:from-[#38F5EF]/30 dark:to-[#FE2A53]/30 rounded-2xl p-8 md:p-12'>
            <div className='max-w-3xl mx-auto text-center'>
              <h5 className='text-2xl md:text-3xl font-bold text-primarycolor dark:text-secondarycolor mb-4'>
                Stay Updated with X-Space
              </h5>
              <p className='text-gray-600 dark:text-gray-300 mb-8'>
                Subscribe to our newsletter to receive the latest updates,
                market insights, and exclusive property listings directly in
                your inbox.
              </p>
              <div className='flex flex-col sm:flex-row gap-4 max-w-xl mx-auto'>
                <Input
                  type='email'
                  placeholder='Your email address'
                  className='flex-grow py-6 dark:bg-white dark:font-bold'
                />
                <Button className='bg-primarycolor hover:bg-secondarycolor text-white hover:text-black  dark:bg-primarycolor dark:hover:bg-secondarycolor dark:text-white dark:hover:text-black  py-6'>
                  Subscribe
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

// Skeleton loaders for loading state
function FeaturedArticleSkeleton() {
  return (
    <Card className='overflow-hidden h-full border-0 shadow-md'>
      <Skeleton className='h-64 w-full' />
      <CardHeader>
        <Skeleton className='h-8 w-3/4 mb-2' />
        <Skeleton className='h-4 w-full mb-1' />
        <Skeleton className='h-4 w-2/3' />
      </CardHeader>
      <CardFooter className='flex justify-between items-center pt-0'>
        <div className='flex items-center gap-4'>
          <Skeleton className='h-4 w-24' />
          <Skeleton className='h-4 w-20' />
        </div>
        <Skeleton className='h-8 w-24' />
      </CardFooter>
    </Card>
  );
}

function ArticleSkeleton() {
  return (
    <Card className='overflow-hidden h-full border-0 shadow-sm'>
      <Skeleton className='h-48 w-full' />
      <CardHeader>
        <Skeleton className='h-6 w-5/6 mb-2' />
      </CardHeader>
      <CardContent>
        <Skeleton className='h-4 w-full mb-1' />
        <Skeleton className='h-4 w-4/5' />
      </CardContent>
      <CardFooter className='flex justify-between items-center pt-0'>
        <Skeleton className='h-4 w-20' />
        <Skeleton className='h-4 w-24' />
      </CardFooter>
    </Card>
  );
}
